// n8n-chat/src/components/ui/sign-up.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";

import { authClient } from "@/lib/auth-client";
import {
  AuthLayout,
  GlassInputWrapper,
  GoogleIcon,
  type Testimonial,
} from "@/components/auth/auth-layout";

const signUpSchema = z
  .object({
    email: z.string().trim().email("Adresse email invalide."),
    password: z.string().min(8, "Minimum 8 caractères."),
    confirmPassword: z.string().min(1, "Confirmation requise."),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Les mots de passe ne correspondent pas.",
  });

type SignUpValues = z.infer<typeof signUpSchema>;
type FieldErrors = Partial<Record<keyof SignUpValues, string>>;

export function SignUpPage({
  title = "Inscription",
  description = "Créez votre compte Stack Flow",
  heroImageSrc,
  testimonials = [],
}: {
  readonly title?: string;
  readonly description?: string;
  readonly heroImageSrc?: string;
  readonly testimonials?: Testimonial[];
}) {
  const router = useRouter();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [values, setValues] = React.useState<SignUpValues>({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [pending, startTransition] = React.useTransition();

  function setField<K extends keyof SignUpValues>(
    key: K,
    value: SignUpValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setFormError(null);
  }

  function validate(payload: SignUpValues): payload is SignUpValues {
    const parsed = signUpSchema.safeParse(payload);
    if (parsed.success) return true;

    const next: FieldErrors = {};
    for (const issue of parsed.error.issues) {
      const k = issue.path[0];
      if (k === "email") next.email = issue.message;
      if (k === "password") next.password = issue.message;
      if (k === "confirmPassword") next.confirmPassword = issue.message;
    }
    setErrors(next);
    return false;
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const payload: SignUpValues = {
      email: values.email.trim(),
      password: values.password,
      confirmPassword: values.confirmPassword,
    };

    if (!validate(payload)) return;

    startTransition(() => {
      authClient.signUp.email(
        {
          name: payload.email.split("@")[0],
          email: payload.email,
          password: payload.password,
          callbackURL: "/dashboard",
        },
        {
          onSuccess: () => {
            router.push("/dashboard");
            router.refresh();
          },
          onError: (ctx) => setFormError(ctx.error.message),
        },
      );
    });
  }

  function onGoogle() {
    setFormError(null);

    startTransition(async () => {
      const { error } = await authClient.signIn.social({
        provider: "google",
        callbackURL: "/dashboard",
      });

      if (error) setFormError(error.message || "Une erreur est survenue.");
    });
  }

  return (
    <AuthLayout heroImageSrc={heroImageSrc} testimonials={testimonials}>
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="mb-1 font-display text-3xl font-semibold leading-tight text-white">
            {title}
          </h1>
          <p className="mb-4 font-sans text-sm text-white/60">{description}</p>
        </div>

        {formError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 font-sans text-xs text-destructive">
            {formError}
          </div>
        ) : null}

        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
              Email
            </label>
            <GlassInputWrapper>
              <input
                name="email"
                type="email"
                value={values.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="votre@email.com"
                autoComplete="email"
                className="w-full rounded-2xl bg-transparent p-3 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
              />
            </GlassInputWrapper>
            {errors.email ? (
              <p className="mt-1 text-xs text-destructive">{errors.email}</p>
            ) : null}
          </div>

          <div>
            <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
              Mot de passe
            </label>
            <GlassInputWrapper>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={(e) => setField("password", e.target.value)}
                  placeholder="Votre mot de passe"
                  autoComplete="new-password"
                  className="w-full rounded-2xl bg-transparent p-3 pr-10 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                  ) : (
                    <Eye className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                  )}
                </button>
              </div>
            </GlassInputWrapper>
            {errors.password ? (
              <p className="mt-1 text-xs text-destructive">{errors.password}</p>
            ) : null}
          </div>

          <div>
            <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
              Confirmer le mot de passe
            </label>
            <GlassInputWrapper>
              <div className="relative">
                <input
                  name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={values.confirmPassword}
                  onChange={(e) => setField("confirmPassword", e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  autoComplete="new-password"
                  className="w-full rounded-2xl bg-transparent p-3 pr-10 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? (
                    <EyeOff className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                  ) : (
                    <Eye className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                  )}
                </button>
              </div>
            </GlassInputWrapper>
            {errors.confirmPassword ? (
              <p className="mt-1 text-xs text-destructive">
                {errors.confirmPassword}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={pending}
            className="w-full rounded-xl bg-[#6729FF] py-3 font-display text-sm font-semibold text-white shadow-[0_0_20px_rgba(103,41,255,0.3)] transition-all duration-300 hover:bg-[#5220cc] hover:shadow-[0_0_30px_rgba(103,41,255,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pending ? "Création..." : "Créer un compte"}
          </button>
        </form>

        <div className="relative my-3 flex items-center justify-center">
          <span className="w-full border-t border-white/10" />
          <span className="absolute bg-black px-3 font-sans text-xs text-white/40">
            Ou continuer avec
          </span>
        </div>

        <button
          type="button"
          onClick={onGoogle}
          disabled={pending}
          className="group flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-3 font-sans text-sm text-white transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <GoogleIcon />
          Google
        </button>

        <p className="mt-2 text-center font-sans text-xs text-white/60">
          Déjà un compte&nbsp;?{" "}
          <Link
            href="/sign-in"
            className="font-sans font-medium text-[#6729FF] transition-colors hover:underline"
          >
            Connexion
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
